import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Drawer, DrawerProps } from '@mui/material';

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

type ResizableRightDrawerProps = Omit<DrawerProps, 'anchor' | 'PaperProps'> & {
  storageKey: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  PaperProps?: DrawerProps['PaperProps'];
};

export default function ResizableRightDrawer({
  storageKey,
  defaultWidth = 900,
  minWidth = 420,
  maxWidth = 1400,
  children,
  PaperProps,
  ...drawerProps
}: ResizableRightDrawerProps) {
  const [width, setWidth] = useState<number>(defaultWidth);
  const draggingRef = useRef(false);

  const bounds = useMemo(() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : maxWidth;
    const max = clamp(maxWidth, minWidth, Math.max(minWidth, vw));
    return { min: minWidth, max };
  }, [minWidth, maxWidth]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      const n = raw ? Number(raw) : NaN;
      if (Number.isFinite(n)) {
        setWidth(clamp(n, bounds.min, bounds.max));
      } else {
        setWidth(clamp(defaultWidth, bounds.min, bounds.max));
      }
    } catch {
      setWidth(clamp(defaultWidth, bounds.min, bounds.max));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    const onResize = () => setWidth((w) => clamp(w, bounds.min, bounds.max));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [bounds.max, bounds.min]);

  const persist = (w: number) => {
    try {
      window.localStorage.setItem(storageKey, String(w));
    } catch {
      // ignore
    }
  };

  return (
    <Drawer
      anchor="right"
      {...drawerProps}
      PaperProps={{
        ...PaperProps,
        sx: {
          width: { xs: '100%', sm: width },
          maxWidth: '100vw',
          ...(PaperProps as any)?.sx,
        },
      }}
    >
      <Box sx={{ position: 'relative', height: '100%' }}>
        {/* Resize handle (left edge) */}
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          onDoubleClick={() => {
            const next = clamp(defaultWidth, bounds.min, bounds.max);
            setWidth(next);
            persist(next);
          }}
          onPointerDown={(e) => {
            // Only allow on non-touch small viewports; on xs we are full width anyway.
            if (window.innerWidth < 600) return;
            draggingRef.current = true;
            (e.currentTarget as any).setPointerCapture?.(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (!draggingRef.current) return;
            const next = clamp(window.innerWidth - e.clientX, bounds.min, bounds.max);
            setWidth(next);
          }}
          onPointerUp={(e) => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            (e.currentTarget as any).releasePointerCapture?.(e.pointerId);
            persist(width);
          }}
          onPointerCancel={() => {
            draggingRef.current = false;
          }}
          sx={{
            display: { xs: 'none', sm: 'block' },
            position: 'absolute',
            left: -6,
            top: 0,
            bottom: 0,
            width: 12,
            cursor: 'col-resize',
            touchAction: 'none',
            zIndex: 10,
            '&:hover': {
              '&::after': { opacity: 1 },
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 5,
              top: 0,
              bottom: 0,
              width: 2,
              borderRadius: 2,
              bgcolor: 'primary.main',
              opacity: 0,
              transition: 'opacity 120ms ease',
            },
          }}
        />

        <Box sx={{ height: '100%' }}>{children}</Box>
      </Box>
    </Drawer>
  );
}

